import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Home, Share2 } from "lucide-react";
import { UI, DS } from "../designSystem";

// Tela de um dia devocional (dentro de uma trilha). Extraída de
// PrototypeApp.js — antes vivia inline como um bloco `if (screen === "day")`
// dentro do componente principal de 5.400 linhas.
//
// Recebe como props tudo que precisa do estado/estilos do app pai: os
// estilos compartilhados (containerStyle, softPanelStyle etc. — os mesmos
// objetos calculados uma vez no componente principal, sem duplicar lógica
// de responsividade aqui), o estado do dia/trilha selecionados, e as funções
// de destaque/registro de progresso que continuam vivendo no componente pai
// (elas mexem em estado compartilhado com outras telas, como o progresso da
// trilha usado também na tela "Rotinas").
export default function DayScreen({
  selectedDay,
  selectedTrail,
  devotionalHighlights,
  devotionalReflections,
  onChangeDevotionalReflection,
  getDevotionalIdentity,
  coopActive,
  dayTab,
  setDayTab,
  handleDevotionalTextSelect,
  renderDevotionalHighlightedText,
  devotionalSelection,
  addDevotionalHighlight,
  markDayAsCompleted,
  onGoToNextDay,
  progressSyncing,
  progressSyncFeedback,
  devotionalFeedbackOptions = [],
  devotionalFeedbackGiven,
  devotionalFeedbackSyncing,
  onSubmitDevotionalFeedback,
  selectedTrailGroup,
  viewerUserId,
  viewerAvatarId,
  AvatarSvg,
  getAvatarById,
  groupDayComments = [],
  groupDayCommentsLoading,
  groupDayCommentDraft,
  onChangeGroupDayCommentDraft,
  groupDayCommentSyncing,
  groupDayCommentFeedback,
  onSubmitGroupDayComment,
  isRealAdmin,
  adminCommentPersonMap = {},
  onBack,
  onGoToDay,
  renderBottomNav,
  isMobile,
  isTablet,
  isDesktop,
  devotionalsByTrail,
  styles
}) {
  const [shareFeedback, setShareFeedback] = useState("");

  const {
    containerStyle,
    fixedTopBarStyle,
    pageContentStyle,
    warmHeroStyle,
    overflowGuardStyle,
    eyebrowStyle,
    h1Style,
    softPanelStyle,
    buttonReset
  } = styles;

  const selectedTrailDevotionals =
    selectedTrail?.devotionals ||
    devotionalsByTrail[selectedTrail?.id] ||
    devotionalsByTrail[selectedTrail?.slug] ||
    [];
  const activeDevotional = selectedTrailDevotionals.find((d) => d.day === selectedDay) || {
    title: "Dia em preparação",
    devotional:
      "Esta leitura ainda está sendo preparada. Enquanto isso, retome o último dia disponível ou escolha outra trilha para continuar sua rotina.",
    question: "Qual tema você gostaria de aprofundar quando este dia estiver disponível?",
    practice: "Volte para a trilha e escolha outro dia disponível para manter sua constância.",
    reflectionPrompt: "Registre aqui o que você espera aprofundar nesta caminhada."
  };
  const devotionalIdentity = getDevotionalIdentity(selectedTrail, selectedDay);
  const activeDevotionalHighlights = devotionalHighlights[devotionalIdentity] || [];
  const activeDevotionalReflection = devotionalReflections[devotionalIdentity] || "";
  const devotionalParagraphs = (activeDevotional.devotional || "").split("\n\n").filter(Boolean);
  const dayAccent = selectedTrail?.color || UI.brand;
  const dayTabs = [
    ["devotional", "Ler"],
    ["question", "Pensar"],
    ["practice", "Praticar"],
    ["reflection", "Registrar"],
    ...(coopActive ? [["chat", "Partilhar"]] : [])
  ];

  // Compartilhar um trecho do dia + o link (pedido do Rodrigo, 20/10/2026):
  // abre o seletor nativo de apps (Web Share API) quando o navegador
  // suporta -- normalmente o caso no celular, onde a maioria le o
  // devocional. Em desktop/navegadores sem suporte, cai pra copiar
  // titulo + trecho + link na area de transferencia (mesmo padrao ja'
  // usado em AdminEscalas.jsx pra escala de musicos).
  async function handleShareDay() {
    const firstParagraph = (devotionalParagraphs[0] || activeDevotional.devotional || "").trim();
    const MAX_EXCERPT = 220;
    const excerpt =
      firstParagraph.length > MAX_EXCERPT
        ? `${firstParagraph.slice(0, MAX_EXCERPT).trim()}...`
        : firstParagraph;
    const link = typeof window !== "undefined" ? window.location.href : "";
    const shareTitle = selectedTrail?.title
      ? `${selectedTrail.title} -- ${activeDevotional.title}`
      : activeDevotional.title || "Devocional";

    if (navigator.share) {
      try {
        await navigator.share({ title: shareTitle, text: excerpt, url: link });
      } catch (_) {
        // compartilhamento cancelado pela pessoa -- nao e' erro, so' ignora
      }
      return;
    }

    const fullText = `${shareTitle}

${excerpt}

${link}`;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(fullText);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = fullText;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      setShareFeedback("Trecho copiado! Cole para compartilhar.");
    } catch (_) {
      setShareFeedback("Não consegui copiar agora -- tenta de novo.");
    }
    setTimeout(() => setShareFeedback(""), 3000);
  }

  return (
    <div style={containerStyle}>
      <div style={{ ...fixedTopBarStyle, borderBottom: `2px solid ${dayAccent}` }}>
        <button onClick={onBack} style={{ ...buttonReset, fontSize: 24, color: "#9CA3AF", marginRight: 20 }}>←</button>
        <span style={{ fontSize: 13, fontWeight: 700, color: "#FFFFFF", textTransform: "uppercase", letterSpacing: 2 }}>DIA {selectedDay}</span>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6 }}>
          <button onClick={handleShareDay} aria-label="Compartilhar este dia" title="Compartilhar" style={{ ...buttonReset, display: "inline-flex", alignItems: "center", justifyContent: "center", width: 34, height: 34, borderRadius: 999, color: "#9CA3AF", flexShrink: 0 }}>
            <Share2 size={17} />
          </button>
          <a href="/" aria-label="Voltar para o site institucional" title="Início do site" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 34, height: 34, borderRadius: 999, color: "#9CA3AF", textDecoration: "none", flexShrink: 0 }}>
            <Home size={18} />
          </a>
        </div>
      </div>

      <div style={pageContentStyle}>
        {shareFeedback && (
          <p style={{ margin: "0 0 14px", padding: "10px 14px", borderRadius: 10, background: "rgba(255,255,255,0.06)", border: `1px solid ${dayAccent}55`, color: "#F2F5FA", fontSize: 12.5, fontWeight: 700 }}>{shareFeedback}</p>
        )}
        <section style={{ ...warmHeroStyle, ...overflowGuardStyle, marginBottom: 18 }}>
          <p style={{ ...eyebrowStyle, color: "#87582b" }}>{selectedTrail?.title || "Devocional"} • Dia {selectedDay}</p>
          <h1 style={{ ...h1Style, fontSize: isDesktop ? 44 : isTablet ? 34 : 29, margin: 0, lineHeight: 1.02, color: "#151b2a", fontWeight: 900 }}>{activeDevotional.title}</h1>
          {activeDevotional.verse && (
            <p style={{ margin: "14px 0 0", maxWidth: 620, fontSize: 14, lineHeight: 1.55, color: "#425064", fontWeight: 800 }}>{activeDevotional.verse}</p>
          )}
          {activeDevotional.verseText && (
            <p style={{ margin: "8px 0 0", maxWidth: 680, fontSize: 16, lineHeight: 1.58, color: "#4c596b" }}>{activeDevotional.verseText}</p>
          )}
        </section>

        {activeDevotional.objective && (
          <section style={{ ...softPanelStyle, padding: isTablet ? "18px 22px" : "16px", marginBottom: 18, borderLeft: `4px solid ${dayAccent}` }}>
            <p style={{ margin: "0 0 7px", fontSize: 11, letterSpacing: 1.4, color: dayAccent, textTransform: "uppercase", fontWeight: 900 }}>Objetivo do dia</p>
            <p style={{ margin: 0, color: "#EAF0F8", fontSize: isTablet ? 17 : 15, lineHeight: 1.55, fontWeight: 700 }}>{activeDevotional.objective}</p>
          </section>
        )}

        <div style={{ display: "flex", gap: 8, marginBottom: 18, overflowX: "auto", paddingBottom: 4 }}>
          {dayTabs.map(([key, label]) => (
            <button key={key} onClick={() => setDayTab(key)} style={{ ...buttonReset, flex: isTablet ? 1 : "0 0 auto", minHeight: 42, padding: "0 14px", borderRadius: 999, border: dayTab === key ? `1px solid ${dayAccent}` : `1px solid ${UI.stroke}`, background: dayTab === key ? `${dayAccent}22` : "rgba(255,255,255,0.045)", fontSize: 11, fontWeight: 900, textTransform: "uppercase", letterSpacing: 0.8, textAlign: "center", transition: "all 0.2s", color: dayTab === key ? "#FFFFFF" : "#9AA6B8" }}>
              {label}
              {key === "chat" && groupDayComments.length > 0 && <span style={{ marginLeft: 6, background: dayAccent, color: "#111", padding: "2px 5px", borderRadius: 10, fontSize: 9, fontWeight: 900 }}>{groupDayComments.length}</span>}
            </button>
          ))}
        </div>

        {/* Navegacao simples entre dias ja alcancados (reler) -- separada do
            bloco "Concluir este dia"/"Continuar a caminhada" mais abaixo, que
            continua so' aparecendo na fronteira real de progresso. Sem isso,
            reabrir um dia antigo (pelo Meu Diario, por ex.) prendia a pessoa
            nesse dia -- so' dava pra mudar de dia voltando pra lista da
            trilha e escolhendo nela de novo. "Dia seguinte" aqui so' libera
            ate o dia ja alcancado (selectedTrail.progress), nunca avanca pra
            um dia ainda nao lido -- isso continua exigindo "Concluir este
            dia". Pedido do Rodrigo, 26/10/2026, testando o Dia 2 de uma
            trilha onde ja tinha avancado mais. */}
        {onGoToDay && (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 18, minHeight: 20 }}>
            {selectedDay > 1 ? (
              <button onClick={() => onGoToDay(selectedDay - 1)} style={{ ...buttonReset, display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 900, color: "#C7D3E6" }}>
                ← Dia {selectedDay - 1}
              </button>
            ) : <span />}
            {selectedTrail && (selectedTrail.progress || 0) > selectedDay ? (
              <button onClick={() => onGoToDay(selectedDay + 1)} style={{ ...buttonReset, display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 900, color: "#C7D3E6" }}>
                Dia {selectedDay + 1} →
              </button>
            ) : <span />}
          </div>
        )}

        {dayTab === "devotional" && (
          <div
            className="devotional-reading-surface"
            onMouseUp={handleDevotionalTextSelect}
            onTouchEnd={handleDevotionalTextSelect}
            style={{ ...overflowGuardStyle, marginTop: 16, background: "#F8F3EA", padding: isTablet ? "38px 42px" : "26px 18px", borderRadius: 18, boxShadow: "0 20px 42px rgba(0,0,0,0.28)", position: "relative", color: "#151b2a" }}
          >
            {devotionalParagraphs.map((p, i) => (
              <p key={i} data-devotional-p-index={i} style={{ ...h1Style, fontWeight: 500, fontSize: isMobile ? 17 : 18, lineHeight: isMobile ? 1.72 : 1.82, color: "#1d2636", margin: i === devotionalParagraphs.length - 1 ? 0 : "0 0 26px" }}>
                {renderDevotionalHighlightedText(p, activeDevotionalHighlights, i)}
              </p>
            ))}
            {devotionalSelection && (
              <div
                className="devotional-highlight-menu"
                style={{
                  position: "fixed",
                  top: devotionalSelection.top,
                  left: devotionalSelection.left,
                  transform: "translateX(-50%)",
                  zIndex: 260,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "8px 10px",
                  borderRadius: 999,
                  background: "rgba(17,24,39,0.96)",
                  border: "1px solid rgba(255,255,255,0.16)",
                  boxShadow: "0 14px 32px rgba(0,0,0,0.32)",
                  backdropFilter: "blur(12px)"
                }}
              >
                {["#FDE68A", "#BAE6FD", "#BBF7D0", "#FBCFE8"].map((color) => (
                  <button
                    key={color}
                    onClick={() => addDevotionalHighlight(color)}
                    aria-label="Marcar texto"
                    style={{ ...buttonReset, width: 26, height: 26, borderRadius: 999, background: color, border: "1px solid rgba(255,255,255,0.45)", boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.08)" }}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {dayTab === "question" && (
          <div style={{ ...softPanelStyle, padding: isTablet ? "34px 32px" : "28px 22px", textAlign: "left", position: "relative", marginTop: 16 }}>
            <p style={{ ...eyebrowStyle, color: dayAccent }}>Pergunta do dia</p>
            <p style={{ ...h1Style, fontSize: isTablet ? 28 : 23, lineHeight: 1.32, color: "#F4F7FB", fontWeight: 800, margin: 0 }}>{activeDevotional.question}</p>
          </div>
        )}

        {dayTab === "practice" && (
          <div style={{ ...softPanelStyle, padding: isTablet ? "32px 30px" : "26px 22px", borderTop: `4px solid ${dayAccent}`, marginTop: 16 }}>
            <h3 style={{ ...eyebrowStyle, color: dayAccent }}>Prática possível</h3>
            {(activeDevotional.practice || "").split("\n").map((line, i) => (
              <p key={i} style={{ fontSize: 16, lineHeight: 1.7, color: "#E7EDF7", margin: "0 0 16px", fontWeight: 600 }}>{line}</p>
            ))}
          </div>
        )}

        {dayTab === "chat" && coopActive && (
          <div style={{ display: "flex", flexDirection: "column", background: UI.panel, border: `1px solid ${UI.stroke}`, borderRadius: UI.radiusMd, overflow: "hidden" }}>
            <div style={{ padding: "12px 16px", background: UI.panelSoft, borderBottom: `1px solid ${UI.stroke}` }}>
              <h3 style={{ margin: 0, fontSize: 14, color: "#FFF", fontWeight: 700 }}>Reflexões da mesa</h3>
              <p style={{ margin: "4px 0 0", fontSize: 11, color: "#6B7280" }}>O que cada pessoa pensou sobre o Dia {selectedDay}</p>
            </div>

            <div style={{ minHeight: 160, maxHeight: 420, padding: 16, overflowY: "auto", display: "flex", flexDirection: "column", gap: 16 }}>
              {groupDayCommentsLoading ? (
                <p style={{ margin: 0, fontSize: 13, color: "#6B7280" }}>Carregando...</p>
              ) : groupDayComments.length ? (
                groupDayComments.map((comment) => {
                  const member = selectedTrailGroup?.members?.find((item) => item.userId === comment.userId);
                  const isYou = Boolean(viewerUserId) && comment.userId === viewerUserId;
                  const name = isYou ? "Você" : (member?.displayName || "Alguém da mesa");
                  const initial = (name || "?").trim().charAt(0).toUpperCase() || "?";
                  const commentAvatarId = isYou ? viewerAvatarId : member?.user?.avatarId;
                  const commentAvatar = typeof getAvatarById === "function" && commentAvatarId ? getAvatarById(commentAvatarId) : null;
                  let timeLabel = "";
                  try {
                    timeLabel = new Date(comment.createdAt).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
                  } catch (error) {
                    timeLabel = "";
                  }
                  return (
                    <div key={comment.id} style={{ display: "flex", gap: 12, alignSelf: isYou ? "flex-end" : "flex-start", maxWidth: "85%", flexDirection: isYou ? "row-reverse" : "row" }}>
                      <div style={{ width: 28, height: 28, borderRadius: 14, background: isYou ? dayAccent : "#333", color: isYou ? "#151b2a" : "#FFF", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, flexShrink: 0, marginTop: 4, overflow: "hidden" }}>
                        {commentAvatar && AvatarSvg ? <AvatarSvg avatar={commentAvatar} size={28} /> : initial}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <p style={{ margin: "0 0 3px", fontSize: 11, color: "#8F9BAD", fontWeight: 800, textAlign: isYou ? "right" : "left" }}>{name}</p>
                        <div style={{ background: UI.panelSoft, padding: "12px 16px", borderRadius: isYou ? "12px 0 12px 12px" : "0 12px 12px 12px", border: `1px solid ${UI.stroke}` }}>
                          <p style={{ margin: 0, fontSize: 14, color: "#D1D5DB", lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{comment.message}</p>
                          {timeLabel && <span style={{ display: "block", marginTop: 6, fontSize: 10, color: "#6B7280" }}>{timeLabel}</span>}
                        </div>
                        {/* So' pra quem e' ADMIN de verdade: liga o comentario ao cadastro
                            (Person) de quem comentou, pra responder "quem e' essa pessoa?"
                            sem sair caçando em outra tela -- ver adminCommentPersonMap em
                            PrototypeApp.js e GET /admin/pessoas-por-user. Nunca mostrado pra
                            quem nao e' admin, nem pro proprio comentario da pessoa. */}
                        {isRealAdmin && !isYou ? (() => {
                          const pessoa = adminCommentPersonMap[comment.userId];
                          if (pessoa === undefined) return null;
                          if (!pessoa) {
                            return (
                              <p style={{ margin: "4px 0 0", fontSize: 10, color: "#6B7280", textAlign: isYou ? "right" : "left" }}>
                                Sem cadastro na secretaria
                              </p>
                            );
                          }
                          const rotaPorTipo = { member: "/admin/membros", visitor: "/admin/visitantes" };
                          const rota = rotaPorTipo[pessoa.tipo];
                          const rotuloPorTipo = { member: "Membro", visitor: "Visitante", child: "Criança" };
                          const rotulo = rotuloPorTipo[pessoa.tipo] || "Cadastro";
                          return rota && pessoa.recordId ? (
                            <Link
                              to={`${rota}?personId=${encodeURIComponent(pessoa.recordId)}`}
                              style={{ display: "block", marginTop: 4, fontSize: 10, color: "#7DD3FC", fontWeight: 700, textAlign: isYou ? "right" : "left", textDecoration: "underline" }}
                            >
                              Ver cadastro ({rotulo}): {pessoa.fullName}
                            </Link>
                          ) : (
                            <p style={{ margin: "4px 0 0", fontSize: 10, color: "#6B7280", textAlign: isYou ? "right" : "left" }}>
                              {rotulo}: {pessoa.fullName}
                            </p>
                          );
                        })() : null}
                      </div>
                    </div>
                  );
                })
              ) : (
                <p style={{ margin: 0, fontSize: 13, color: "#6B7280", lineHeight: 1.5 }}>Ninguém comentou este dia ainda. Seja a primeira pessoa da mesa a compartilhar o que esse dia te mostrou.</p>
              )}
            </div>

            {groupDayCommentFeedback && (
              <p style={{ margin: "0 16px 10px", fontSize: 12, color: "#F87171" }}>{groupDayCommentFeedback}</p>
            )}

            <div style={{ display: "flex", gap: 8, padding: 16, borderTop: `1px solid ${UI.stroke}` }}>
              <input
                type="text"
                value={groupDayCommentDraft || ""}
                onChange={(event) => onChangeGroupDayCommentDraft?.(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    onSubmitGroupDayComment?.();
                  }
                }}
                placeholder="Compartilhe o que esse dia te mostrou..."
                style={{ flex: 1, minHeight: 42, padding: "0 14px", borderRadius: 999, border: `1px solid ${UI.stroke}`, background: "rgba(255,255,255,0.055)", color: "#FFFFFF", fontSize: 14, outline: "none", boxSizing: "border-box" }}
              />
              <button
                onClick={() => onSubmitGroupDayComment?.()}
                disabled={groupDayCommentSyncing || !(groupDayCommentDraft || "").trim()}
                style={{ ...buttonReset, minHeight: 42, padding: "0 18px", borderRadius: 999, background: dayAccent, color: "#151b2a", fontSize: 12, fontWeight: 950, whiteSpace: "nowrap", opacity: (groupDayCommentSyncing || !(groupDayCommentDraft || "").trim()) ? 0.55 : 1 }}
              >
                {groupDayCommentSyncing ? "Enviando..." : "Enviar"}
              </button>
            </div>
          </div>
        )}

        {dayTab === "reflection" && (
          <div style={{ ...softPanelStyle, padding: isTablet ? "30px" : "22px" }}>
            <h3 style={{ ...eyebrowStyle, color: "#C7D3E6" }}>Seu registro</h3>
            <p style={{ fontSize: 14, color: "#AEB9CB", margin: "0 0 18px", lineHeight: 1.55 }}>{activeDevotional.reflectionPrompt || "Construa aqui as memórias do que Deus te mostrou hoje."}</p>
            <textarea
              value={activeDevotionalReflection}
              onChange={(event) => onChangeDevotionalReflection(event.target.value)}
              placeholder="Escreva sua reflexão..."
              style={{ width: "100%", minHeight: 190, padding: 20, background: "rgba(255,255,255,0.055)", border: `1px solid ${UI.stroke}`, borderRadius: 14, color: "#FFFFFF", fontSize: 16, lineHeight: 1.7, fontFamily: DS.type.family, resize: "vertical", outline: "none", boxSizing: "border-box", marginBottom: 0 }}
            />
          </div>
        )}

        {selectedTrail && selectedDay > (selectedTrail.progress || 0) && (
          <section style={{ marginTop: 22, padding: isTablet ? "18px 20px" : "16px", borderRadius: 18, border: "1px solid rgba(255,255,255,0.12)", background: "linear-gradient(145deg, rgba(255,255,255,0.07), rgba(255,255,255,0.025))", display: "flex", flexDirection: isTablet ? "row" : "column", alignItems: isTablet ? "center" : "stretch", gap: 14, boxShadow: "inset 0 1px 0 rgba(255,255,255,0.05)" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: "0 0 5px", fontSize: 11, letterSpacing: 1.2, textTransform: "uppercase", color: dayAccent, fontWeight: 900 }}>Registrar caminhada</p>
              <p style={{ margin: 0, color: "#D8E0EE", fontSize: 13, lineHeight: 1.45 }}>Quando terminar a leitura, registre este dia e continue sua trilha no próximo encontro.</p>
            </div>
            <button
              onClick={markDayAsCompleted}
              disabled={progressSyncing}
              style={{
                ...buttonReset,
                minHeight: 42,
                padding: "0 18px",
                borderRadius: 999,
                border: `1px solid ${dayAccent}`,
                background: progressSyncing ? "rgba(255,255,255,0.08)" : `${dayAccent}24`,
                color: progressSyncing ? "#9CA3AF" : "#F8E7C4",
                fontSize: 12,
                fontWeight: 900,
                letterSpacing: 0.4,
                whiteSpace: "nowrap",
                boxShadow: progressSyncing ? "none" : `0 10px 20px ${dayAccent}18`,
                opacity: progressSyncing ? 0.75 : 1
              }}
            >
              {progressSyncing ? "Salvando..." : "Concluir este dia"}
            </button>
          </section>
        )}
        {progressSyncFeedback && (
          <p style={{ margin: "10px 0 0", fontSize: 12, color: "#c7ced9" }}>{progressSyncFeedback}</p>
        )}

        {/* "Proximo dia" -- so' aparece na fronteira de progresso de verdade
            (o dia que acabou de ser concluido), nao em qualquer dia antigo
            ja feito que a pessoa volte a abrir pra reler. Sem trava de
            calendario (ver getTrailNextDay em PrototypeApp.js): o proximo
            dia ja esta liberado assim que este for concluido. Pedido do
            Rodrigo, testando ao vivo: "como vai para o dia 3? ja salvei o
            progresso". */}
        {selectedTrail && onGoToNextDay && selectedDay === (selectedTrail.progress || 0) && (
          <section style={{ marginTop: 14, padding: isTablet ? "18px 20px" : "16px", borderRadius: 18, border: `1px solid ${dayAccent}55`, background: `linear-gradient(145deg, ${dayAccent}1f, rgba(255,255,255,0.03))`, display: "flex", flexDirection: isTablet ? "row" : "column", alignItems: isTablet ? "center" : "stretch", gap: 14 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: "0 0 5px", fontSize: 11, letterSpacing: 1.2, textTransform: "uppercase", color: dayAccent, fontWeight: 900 }}>
                {selectedDay >= (selectedTrail.duration || selectedDay) ? "Trilha concluída" : "Continuar a caminhada"}
              </p>
              <p style={{ margin: 0, color: "#D8E0EE", fontSize: 13, lineHeight: 1.45 }}>
                {selectedDay >= (selectedTrail.duration || selectedDay)
                  ? "Você terminou todos os dias desta trilha. Pode escolher outra trilha ou rever este percurso na Mesa."
                  : `Dia ${selectedDay} registrado. Quando quiser, siga para o Dia ${selectedDay + 1}.`}
              </p>
            </div>
            <button
              onClick={onGoToNextDay}
              style={{
                ...buttonReset,
                minHeight: 42,
                padding: "0 18px",
                borderRadius: 999,
                border: `1px solid ${dayAccent}`,
                background: `${dayAccent}2e`,
                color: "#F8E7C4",
                fontSize: 12,
                fontWeight: 900,
                letterSpacing: 0.4,
                whiteSpace: "nowrap",
                boxShadow: `0 10px 20px ${dayAccent}18`
              }}
            >
              {selectedDay >= (selectedTrail.duration || selectedDay) ? "Voltar pra Mesa" : `Ir para o Dia ${selectedDay + 1}`}
            </button>
          </section>
        )}

        {/* Feedback leve de 1 pergunta, so depois que o dia foi registrado --
            pedido do Rodrigo: hoje o feed "Trilhas novas" e uma lista fixa no
            codigo, sem sinal real de leitura/engajamento. Some sozinho depois
            de respondido (fica guardado, nao pergunta de novo). */}
        {selectedTrail && selectedDay <= (selectedTrail.progress || 0) && onSubmitDevotionalFeedback && (
          <section style={{ marginTop: 14, padding: isTablet ? "18px 20px" : "16px", borderRadius: 18, border: "1px solid rgba(255,255,255,0.10)", background: "rgba(255,255,255,0.03)" }}>
            {devotionalFeedbackGiven ? (
              <p style={{ margin: 0, color: "#AEB9CB", fontSize: 13, lineHeight: 1.5 }}>Obrigado pelo retorno sobre este dia.</p>
            ) : (
              <>
                <p style={{ margin: "0 0 10px", color: "#D8E0EE", fontSize: 13, lineHeight: 1.45 }}>Isso fez sentido pra você?</p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {devotionalFeedbackOptions.map((option) => (
                    <button
                      key={option.id}
                      onClick={() => onSubmitDevotionalFeedback(option.id)}
                      disabled={devotionalFeedbackSyncing}
                      style={{
                        ...buttonReset,
                        minHeight: 36,
                        padding: "0 14px",
                        borderRadius: 999,
                        border: "1px solid rgba(255,255,255,0.18)",
                        background: "rgba(255,255,255,0.06)",
                        color: "#F2F5FA",
                        fontSize: 12,
                        fontWeight: 800,
                        opacity: devotionalFeedbackSyncing ? 0.6 : 1
                      }}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </section>
        )}
      </div>
      {renderBottomNav()}
    </div>
  );
}
